import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { checkSymbolSearch, updateItems } from "@shared/utils/misc";
import { toggleArrayItem } from "@shared/utils/arrays";
import { isSymbol } from "@shared/types/utils";
import { usePairsSelectors } from "@shared/features/bots/BotForm/common/hooks";
import { BotType } from "@shared/types";

interface useMultiSymbolsSelectorProps {
  searchValue?: string;
  setSearchValue?: (searchValue?: string) => void;
}

const useMultiSymbolsSelector = ({
  searchValue: propsSearchValue,
  setSearchValue: propsSetSearchValue,
}: useMultiSymbolsSelectorProps) => {
  const {
    symbols,
    setSymbols,
    account,
    botType,
    symbolsMetadata,
    includeNewSymbols,
    setIncludeNewSymbols,
  } = useContext(BotFormContext)!;
  const parsedAccount = isSymbol(account) ? undefined : account;
  const [_searchValue, _setSearchValue] = useState("");
  const searchValue = propsSearchValue ?? _searchValue;
  const setSearchValue = propsSetSearchValue ?? _setSearchValue;
  const [isOpen, setIsOpen] = useState(false);
  const searchInputRef = useRef<any>(undefined);
  const exchange = isSymbol(account) ? undefined : account?.exchange;
  const exchangeSymbols = useMemo(() => {
    return (
      symbolsMetadata?.getAccountActiveSymbols(parsedAccount?.client_id) || []
    );
  }, [symbolsMetadata, parsedAccount]);
  const inputHoverRef = useRef(false);

  useEffect(() => {
    if (!isOpen && searchValue) setSearchValue("");
    else if (isOpen) searchInputRef?.current?.focus();
  }, [isOpen]);

  const symbolsSet = useMemo(() => {
    return new Set(isSymbol(symbols) ? undefined : symbols);
  }, [symbols]);

  const { pairOptions } = usePairsSelectors({
    allSymbols: exchangeSymbols,
    selectedSymbols: symbolsSet,
    setSelectedSymbols: setSymbols,
  });

  const filteredSymbols = useMemo(() => {
    if (!searchValue) return exchangeSymbols;
    return exchangeSymbols.filter((symbol) =>
      checkSymbolSearch(symbol, searchValue)
    );
  }, [exchangeSymbols, searchValue]);

  const updateSymbols = (symbols: Set<string> | string[], add: boolean) => {
    updateItems(symbolsSet, symbols, add, setSymbols);
  };

  const onSymbolClick = (symbol: string) => {
    if (!isSymbol(symbols)) {
      setSymbols(toggleArrayItem(symbols, symbol));
    }
  };

  const symbolOptions: SymbolOptionData[] = useMemo(() => {
    return filteredSymbols.map((symbol) => {
      return {
        symbol,
        isSelected: symbolsSet.has(symbol),
        onClick: () => onSymbolClick(symbol),
        key: symbol,
      };
    });
  }, [symbols, filteredSymbols]);

  const searchInputData = {
    ref: searchInputRef,
    value: searchValue,
    onChange: setSearchValue,
    onMouseOver: () => (inputHoverRef.current = true),
    onMouseOut: () => (inputHoverRef.current = false),
  };

  const selectAllData = useMemo(() => {
    const isAllSelected = filteredSymbols.every((symbol) =>
      symbolsSet.has(symbol)
    );
    return {
      text: `${isAllSelected ? "Unselect" : "Select"} all ${
        searchValue ? "matching" : ""
      }`,
      onClick: () => updateSymbols(filteredSymbols, !isAllSelected),
      isSelected: isAllSelected,
      key: "selectAll",
      symbol: "selectAll",
    };
  }, [filteredSymbols, symbolsSet]);

  return {
    pairOptions,
    symbolOptions,
    setIsOpen,
    searchInputData,
    selectAllData,
    isOpen,
    inputHoverRef,
    exchange,
    symbols,
    isSignalsBot: botType === BotType.SIGNAL,
    includeNewSymbols,
    setIncludeNewSymbols,
    account,
  };
};

export default useMultiSymbolsSelector;

export type SymbolOptionData = {
  symbol: string;
  isSelected: boolean;
  onClick: () => void;
};
